"use client";

import { useEffect, useRef, useState } from "react";

import Image from "next/image";

import { Bookmark } from "lucide-react";
import { createPortal } from "react-dom";

import SaveCollectionsDropdown from "@/components/collections/SaveCollectionsDropdown";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCollections } from "@/contexts/CollectionsContext";
import { useSavedProducts } from "@/contexts/SavedProductsContext";
import { useSignUpPrompt } from "@/contexts/SignUpPromptContext";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { type Product, getProductImage, getProductName } from "@/lib/product-utils";
import { tailwindBreakpoints } from "@/styles/design-tokens";
import { subscribeToMediaQuery } from "@/utils/match-media";

// Global registry for batch checking
// Batch checking registry - aggregates product membership checks across multiple SaveButton
// instances to minimize API calls. Uses module-level state to coordinate batching.
// Note: This pattern works for client-side rendering but may cause issues with SSR or
// concurrent React features. Consider moving to a context provider if issues arise.
const pendingChecks = new Map<string, Set<string>>(); // collectionId -> Set of productIds
const checkCallbacks = new Map<string, Array<(results: Record<string, boolean>) => void>>();
let batchTimeout: ReturnType<typeof setTimeout> | null = null;

// UI constants
const DROPDOWN_AUTO_HIDE_MS = 30000; // 30 seconds
const BATCH_CHECK_DELAY_MS = 50; // Debounce for batching collection membership checks
const DROPDOWN_WIDTH_PX = 256; // w-64 in Tailwind

interface SaveButtonProps {
  productId: string;
  product?: Product;
  className?: string;
  variant?: "icon" | "text" | "tag";
  alwaysShowSavedLabel?: boolean;
  defaultCollectionId?: string; // For quest pages
  // Optional: external chooser can specify a target collection (besides default)
  targetCollectionId?: string;
  onHoverChange?: (hovered: boolean) => void;
  simpleToggle?: boolean; // If true, click toggles save in default collection (no dropdown)
}

/** Mobile bottom sheet for save-to-collection - shared between tag and icon variants */
function SaveMobileSheet({
  open,
  onOpenChange,
  productId,
  product,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  product?: Product;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl px-0 pb-0">
        <SheetHeader className="px-5 pb-3">
          <SheetTitle className="text-base font-medium">Save to Collection</SheetTitle>
        </SheetHeader>
        {/* Minimal product preview */}
        {product && (
          <div className="mx-5 mb-4 flex items-center gap-3 rounded-lg bg-gray-50 p-2.5">
            {getProductImage(product) && (
              <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-white">
                <Image
                  src={getProductImage(product) || ""}
                  alt={getProductName(product) || "Product"}
                  fill
                  className="object-cover"
                  sizes="40px"
                />
              </div>
            )}
            <span className="min-w-0 truncate text-sm text-gray-700">
              {getProductName(product)}
            </span>
          </div>
        )}
        <div className="px-5">
          <SaveCollectionsDropdown
            productId={productId}
            onClose={() => onOpenChange(false)}
            className="w-full !p-0"
            hideDoneButton
          />
        </div>
        <div
          className="mt-4 border-t border-gray-100 px-5 pt-4"
          style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
        >
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-12 w-full text-sm font-medium"
          >
            Done
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const SaveButton: React.FC<SaveButtonProps> = ({
  productId,
  product,
  className = "",
  variant = "icon",
  alwaysShowSavedLabel = false,
  defaultCollectionId,
  targetCollectionId,
  onHoverChange,
  simpleToggle = false,
}) => {
  const { isAnonymous } = useAuth();
  const { showSignUpPrompt } = useSignUpPrompt();
  const [isHovered, setIsHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [savedInCollections, setSavedInCollections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isInputActive, setIsInputActive] = useState(false);
  // Single ref for the button - used for both click-outside detection and dropdown positioning
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    collections,
    // addProductToCollection, // Only used in commented out code
    // removeProductFromCollection, // Only used in commented out code
    bulkCheckProductsInCollection,
    // createCollection, // Only used in commented out code
  } = useCollections();
  const { isSaved, toggleSaved /* , loading: savedLoading */ } = useSavedProducts();
  const [portalPosition, setPortalPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const defaultCollection = collections.find((c) => c.isDefault);
  const activeCollectionId =
    targetCollectionId || defaultCollectionId || defaultCollection?.id || collections[0]?.id || "";
  const isSavedInActiveCollection = savedInCollections.has(activeCollectionId);
  // Icon fill/state follows global default collection state to update instantly after save/unsave
  const savedForUi = isSaved(productId);

  // Register for batch checking
  useEffect(() => {
    if (!hasChecked && collections.length > 0) {
      const saved = new Set<string>();

      // Create a promise for each collection check
      const checkPromises = collections.map((collection) => {
        return new Promise<void>((resolve) => {
          // Add to pending checks
          if (!pendingChecks.has(collection.id)) {
            pendingChecks.set(collection.id, new Set());
          }
          const pendingSet = pendingChecks.get(collection.id);
          if (pendingSet) {
            pendingSet.add(productId);
          }

          // Register callback
          const key = `${collection.id}:${productId}`;
          if (!checkCallbacks.has(key)) {
            checkCallbacks.set(key, []);
          }
          const callbacks = checkCallbacks.get(key);
          if (callbacks) {
            callbacks.push((results) => {
              if (results[productId]) {
                saved.add(collection.id);
              }
              resolve();
            });
          }
        });
      });

      // Schedule batch check
      if (batchTimeout) {
        clearTimeout(batchTimeout);
      }

      batchTimeout = setTimeout(async () => {
        // Process all pending checks
        for (const [collectionId, productIds] of pendingChecks.entries()) {
          if (productIds.size > 0) {
            try {
              const results = await bulkCheckProductsInCollection(
                collectionId,
                Array.from(productIds)
              );

              // Call all callbacks for this collection
              for (const prodId of productIds) {
                const key = `${collectionId}:${prodId}`;
                const callbacks = checkCallbacks.get(key) || [];
                callbacks.forEach((cb) => cb(results));
                checkCallbacks.delete(key);
              }
            } catch (err) {
              console.error(`Failed to bulk check products for collection ${collectionId}:`, err);
            }
          }
        }
        pendingChecks.clear();
      }, BATCH_CHECK_DELAY_MS);

      // Wait for all checks to complete
      Promise.all(checkPromises).then(() => {
        setSavedInCollections(saved);
        setHasChecked(true);
      });
    }

    return () => {
      // Cleanup callbacks on unmount
      collections.forEach((collection) => {
        const key = `${collection.id}:${productId}`;
        checkCallbacks.delete(key);
      });
    };
  }, [collections, productId, bulkCheckProductsInCollection, hasChecked]);

  // Auto-hide dropdown after 30 seconds (desktop only - mobile uses sheet which has explicit Done)
  // Paused when user is typing a new collection name
  useEffect(() => {
    if (showDropdown && !isMobile && !isInputActive) {
      // Clear any existing timeout
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }

      dropdownTimeoutRef.current = setTimeout(() => {
        setShowDropdown(false);
      }, DROPDOWN_AUTO_HIDE_MS);
    }

    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [showDropdown, isMobile, isInputActive]);

  // Mobile detection
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(`(max-width: ${tailwindBreakpoints.sm - 1}px)`);
    const sync = () => setIsMobile(mql.matches);
    sync();
    const unsub = subscribeToMediaQuery(mql, sync);
    return () => unsub();
  }, []);

  // Lock body scroll when dropdown is open (desktop only - mobile Sheet handles its own)
  useBodyScrollLock({
    key: `save-button-dropdown:${productId}`,
    enabled: showDropdown && !isMobile,
  });

  // Compute fixed position for portal dropdown
  useEffect(() => {
    const compute = () => {
      const el = buttonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dropdownWidth = DROPDOWN_WIDTH_PX;
      const margin = 8; // mt-2
      // Position relative to viewport for position: fixed
      let left = rect.right - dropdownWidth;
      // keep within viewport horizontally
      left = Math.min(window.innerWidth - dropdownWidth - margin, Math.max(margin, left));
      const top = rect.bottom + margin;
      setPortalPosition({ top, left });
    };
    if (showDropdown) {
      compute();
      window.addEventListener("resize", compute);
      window.addEventListener("scroll", compute, true);
      return () => {
        window.removeEventListener("resize", compute);
        window.removeEventListener("scroll", compute, true);
      };
    }
  }, [showDropdown]);

  // When opening dropdown, re-check membership across collections to show up-to-date checkboxes
  useEffect(() => {
    if (showDropdown) {
      setHasChecked(false);
      setSavedInCollections(new Set());
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside or pressing Escape (desktop only - mobile uses sheet)
  useEffect(() => {
    if (isMobile) return; // Mobile uses Sheet with its own close handling

    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Don't close dropdown if user is typing in an input (let input handle Escape)
        const activeEl = document.activeElement;
        if (activeEl?.tagName === "INPUT" || activeEl?.tagName === "TEXTAREA") return;
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDropdown, isMobile]);

  const handleButtonClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Check if user is anonymous - show sign up modal
    if (isAnonymous) {
      showSignUpPrompt("collection");
      return;
    }

    if (loading) return;

    // Simple toggle mode: directly toggle default collection without dropdown
    if (simpleToggle) {
      setLoading(true);
      try {
        await toggleSaved(productId);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Ensure it's saved to wishlist (default collection) before opening dropdown
    setLoading(true);
    try {
      if (!savedForUi) {
        await toggleSaved(productId);
      }
      // Show the dropdown to allow selecting collections
      setShowDropdown((prev) => !prev);
    } finally {
      setLoading(false);
    }
  };

  // const handleCheckboxChange = async (
  //   collectionId: string,
  //   checked: boolean
  // ) => {
  //   if (loading) return;
  //
  //   setLoading(true);
  //   try {
  //     if (checked) {
  //       // Add to selected collection
  //       const success = await addProductToCollection(collectionId, productId);
  //       if (success) {
  //         setSavedInCollections((prev) => {
  //           const newSet = new Set(prev);
  //           newSet.add(collectionId);
  //           return newSet;
  //         });
  //       }
  //     } else {
  //       // Removing from a specific collection
  //       const success = await removeProductFromCollection(
  //         collectionId,
  //         productId
  //       );
  //       if (success) {
  //         setSavedInCollections((prev) => {
  //           const newSet = new Set(prev);
  //           newSet.delete(collectionId);
  //           return newSet;
  //         });
  //       }
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleCreateNewCollection = async () => {
  //   if (!newCollectionName.trim()) return;
  //
  //   setLoading(true);
  //   try {
  //     const newCollection = await createCollection(newCollectionName.trim());
  //     if (newCollection) {
  //       // Add product to the new collection
  //       await addProductToCollection(newCollection.id, productId);
  //       setSavedInCollections((prev) => {
  //         const newSet = new Set(prev);
  //         newSet.add(newCollection.id);
  //         return newSet;
  //       });
  //     }
  //     // Keep dropdown open after creating a collection
  //     setNewCollectionName("");
  //     setShowNewCollectionInput(false);
  //     setShowDropdown(true);
  //     // Reset the auto-hide timer so the user has time to interact
  //     if (dropdownTimeoutRef.current) {
  //       clearTimeout(dropdownTimeoutRef.current);
  //     }
  //     dropdownTimeoutRef.current = setTimeout(() => {
  //       setShowDropdown(false);
  //     }, 8000);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Tag variant – with dropdown
  if (variant === "tag") {
    // Use activeCollectionId which already considers targetCollectionId
    const isSavedInTargetCollection = isSavedInActiveCollection;
    const isFinalSaved = isSavedInTargetCollection;
    // Only show a text label during the brief confirmation; otherwise icon-only
    const label = alwaysShowSavedLabel && isSavedInTargetCollection ? "Saved" : null;

    return (
      <div
        className="relative"
        ref={buttonRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Combined save button */}
        <button
          className={`flex items-center justify-center ${
            label
              ? "rounded-full bg-black px-3 py-1.5 text-xs font-medium text-white"
              : `${isFinalSaved ? "bg-white" : isHovered ? "bg-gray-100" : "bg-white"} rounded-full p-2 text-gray-700 shadow-sm hover:shadow-md`
          } transition-all ${className}`}
          onClick={handleButtonClick}
          onMouseEnter={() => {
            setIsHovered(true);
            onHoverChange?.(true);
          }}
          onMouseLeave={() => {
            setIsHovered(false);
            onHoverChange?.(false);
          }}
          data-testid="save-button"
          aria-label="Save to collection"
        >
          {label ? (
            <span className="flex items-center gap-1">{label}</span>
          ) : (
            <Bookmark className={`h-4 w-4 stroke-[1.5] ${savedForUi ? "fill-current" : ""}`} />
          )}
        </button>

        {/* Mobile: Bottom Sheet */}
        {isMobile && (
          <SaveMobileSheet
            open={showDropdown}
            onOpenChange={setShowDropdown}
            productId={productId}
            product={product}
          />
        )}

        {/* Desktop: Floating Dropdown (Portal) */}
        {!isMobile &&
          showDropdown &&
          portalPosition &&
          createPortal(
            <div
              className="fixed z-[1000] w-64 rounded-xl border border-gray-200 bg-white shadow-lg"
              style={{ top: portalPosition.top, left: portalPosition.left }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <SaveCollectionsDropdown
                productId={productId}
                onClose={() => setShowDropdown(false)}
                onInputActiveChange={setIsInputActive}
              />
            </div>,
            document.body
          )}
      </div>
    );
  }

  // Icon variant with dropdown
  if (variant === "icon") {
    return (
      <div
        className="relative"
        ref={buttonRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 ${
            savedForUi
              ? "bg-white text-gray-900 opacity-100 shadow-md"
              : "bg-transparent text-gray-900 opacity-80 hover:text-gray-700 hover:opacity-100"
          } ${className}`}
          onClick={handleButtonClick}
          data-testid="save-button"
          aria-label="Save to a collection"
          style={{
            filter: savedForUi ? "none" : "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
          }}
        >
          <Bookmark className={`h-4 w-4 stroke-[1.5] ${savedForUi ? "fill-current" : ""}`} />
        </button>

        {/* Mobile: Bottom Sheet */}
        {!simpleToggle && isMobile && (
          <SaveMobileSheet
            open={showDropdown}
            onOpenChange={setShowDropdown}
            productId={productId}
            product={product}
          />
        )}

        {/* Desktop: Floating Dropdown (Portal) */}
        {!simpleToggle &&
          !isMobile &&
          showDropdown &&
          portalPosition &&
          createPortal(
            <div
              className="fixed z-[1000] w-64 rounded-xl border border-gray-200 bg-white shadow-lg"
              style={{ top: portalPosition.top, left: portalPosition.left }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <SaveCollectionsDropdown
                productId={productId}
                onClose={() => setShowDropdown(false)}
                onInputActiveChange={setIsInputActive}
              />
            </div>,
            document.body
          )}
      </div>
    );
  }

  // Text variant
  return (
    <button
      className={`flex items-center justify-center rounded-full bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 ${className}`}
      onClick={handleButtonClick}
      data-testid="save-button"
      aria-label="Save to a collection"
      disabled={loading}
    >
      {isSavedInActiveCollection ? "Saved" : "Save"}
    </button>
  );
};

export default SaveButton;
